import './styles.css'
import { World, xpForLevel } from '../sim/world'
import { DASH_CD } from '../sim/systems'
import { Renderer } from '../render/renderer'
import { rotateIntent } from '../render/camerafx'
import { WEAPONS, WEAPON_LIST } from '../data/weapons'
import { ITEMS, ITEM_LIST } from '../data/items'
import { ENEMY_LIST } from '../data/enemies'
import { TOMES, TOME_LIST } from '../data/tomes'
import { CHALLENGE_LIST } from '../data/challenges'
import { CHARACTER_LIST, CHARACTERS } from '../data/characters'
import { MAP_LIST, MAPS, DIFFICULTIES } from '../data/maps'
import { ENEMIES } from '../data/enemies'
import { ACHIEVEMENTS } from '../data/achievements'
import { t, tName, tBlurb, tPassive, tDesc, tHint, langLabel } from '../i18n'
import { MetaState, META_UPGRADES, upgradeCost } from '../meta/meta'
import { UpgradeOption, optionKey, isBanishable, isLockable, MAX_WEAPONS, MAX_ITEMS, MAX_TOMES } from '../data/upgrades'
import { terrainHeight, terrainSlope, reliefShade } from '../core/terrain'
import { dayLight } from '../core/daylight'
import { vignetteLevels } from '../render/vignette'
import { iconHTML, mapIconSprite, weaponIconSprite, itemIconSprite, tomeIconSprite, metaIconSprite, chromeIconHTML, achievementIconSprite, challengeIconSprite } from '../render/icon'
import { heroSpriteName } from '../render/pixelart'
import { characterIconHTML } from '../render/charicon'
import { chestSpinDelays } from './chestfx'
import { isTouchDevice } from './touchcontrols'
import { isStandalone, triggerInstall } from './pwa'

/**
 * Pixel-art icon for any draft/shop pick. Maps each kind to its original sprite (evolutions → their target
 * weapon; heal/banish/gold → chrome glyphs). Never renders an emoji — empty string if nothing matches.
 */
function optionIconHTML(o: { kind?: string; id?: string; icon?: string }, size: number): string {
  const fb = '' // PT-art: no emoji fallbacks anywhere
  switch (o.kind) {
    case 'weapon-up':
    case 'weapon-new':
      return iconHTML(weaponIconSprite(o.id ?? ''), { size, fallback: fb })
    case 'evolve':
      return iconHTML(weaponIconSprite(WEAPONS[o.id ?? '']?.evolvesTo ?? o.id ?? ''), { size, fallback: fb })
    case 'item-up':
    case 'item-new':
      return iconHTML(itemIconSprite(o.id ?? ''), { size, fallback: fb })
    case 'tome-up':
    case 'tome-new':
      return iconHTML(tomeIconSprite(o.id ?? ''), { size, fallback: fb })
    case 'heal':
      return chromeIconHTML('heal', size)
    case 'banish':
      return chromeIconHTML('banish', size)
    case 'gold':
      return chromeIconHTML('gold', size)
    default:
      return fb
  }
}

/**
 * Clarity pass: a draft/shop/chest card's CATEGORY ribbon (weapon / item / tome / evolution). Separate from the rarity
 * frame and the level tag so a tome reads as a tome even on a tome-UP card (which otherwise shows a generic "LV n" like
 * an item). Color-matched to the HUD loadout coding (weapon amber / item green / tome violet / evolve legendary).
 */
function optionCatClass(kind?: string): string {
  switch (kind) {
    case 'weapon-new': case 'weapon-up': return 'cat-weapon'
    case 'item-new': case 'item-up': return 'cat-item'
    case 'tome-new': case 'tome-up': return 'cat-tome'
    case 'evolve': return 'cat-evolve'
    default: return '' // heal / gold — no category
  }
}
function catRibbon(kind?: string): string {
  const cls = optionCatClass(kind)
  if (!cls) return ''
  const key = cls === 'cat-weapon' ? 'cat_weapon' : cls === 'cat-item' ? 'cat_item' : cls === 'cat-tome' ? 'cat_tome' : 'cat_evolve'
  return `<div class="cat-ribbon ${cls}">${t(key)}</div>`
}

export interface ShopOffer {
  icon: string
  kind?: string // PT-art: drives the pixel-art icon (weapon/item/tome/heal/banish)
  id?: string
  name: string
  rarity: import('../sim/types').Rarity
  desc: string
  price: number
  sold: boolean
  apply: (world: World) => void
  locked?: boolean // kept across a reroll
  fixed?: boolean // staple offer (heal/banish) — not rerollable or lockable
}

export interface LevelUpState {
  rerollCost: number
  canReroll: boolean
  banishCharges: number
  lockedKey: string | null
  gold: number
}
export interface LevelUpHandlers {
  pick: (o: UpgradeOption) => void
  reroll: () => void
  banish: (o: UpgradeOption) => void
  lock: (o: UpgradeOption) => void
  skip: () => void
}

function fmtTime(t: number): string {
  const m = Math.floor(t / 60)
  const s = Math.floor(t % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

/**
 * Char-select "next unlock" progress (Megabonk-style progression hook). DISPLAY-ONLY mirror of the meta-trackable
 * unlock thresholds in game.ts checkUnlocks() — keep the two in sync. Characters whose condition isn't a persisted meta
 * stat (glasscannon = highest hit, swarm = weapons in one run) have no entry and just show their unlock hint as before.
 */
const UNLOCK_PROGRESS: Record<string, { stat: 'bestLevel' | 'bestTime' | 'totalGoldEarned' | 'totalKills' | 'bossesKilled'; target: number; time?: boolean }> = {
  witch: { stat: 'bestLevel', target: 15 },
  frost: { stat: 'bestTime', target: 300, time: true },
  gambler: { stat: 'totalGoldEarned', target: 1000 },
  vampire: { stat: 'bossesKilled', target: 1 },
  berserker: { stat: 'bestLevel', target: 20 },
  engineer: { stat: 'totalGoldEarned', target: 2500 },
  monk: { stat: 'bestTime', target: 480, time: true },
  gunner: { stat: 'totalKills', target: 3000 },
  juggernaut: { stat: 'bestTime', target: 600, time: true },
  sustain: { stat: 'totalGoldEarned', target: 5000 },
  executioner: { stat: 'bossesKilled', target: 5 },
}
function unlockBar(id: string, meta: MetaState): string {
  const u = UNLOCK_PROGRESS[id]
  if (!u) return ''
  const cur = (meta[u.stat] as number) || 0
  const pct = Math.max(0, Math.min(1, cur / u.target))
  const fmt = (n: number) => (u.time ? fmtTime(n) : `${Math.floor(n)}`)
  return `<div class="unlock-prog"><div class="unlock-bar"><div class="unlock-fill" style="width:${(pct * 100).toFixed(0)}%"></div></div><span class="unlock-num">${fmt(Math.min(cur, u.target))} / ${fmt(u.target)}</span></div>`
}

export class UI {
  private hud: HTMLDivElement
  private floaters: HTMLDivElement
  private overlay: HTMLDivElement
  private toastHost: HTMLDivElement

  // cached hud nodes
  private elTimer!: HTMLElement
  private elXp!: HTMLElement
  private elLevel!: HTMLElement
  private elHp!: HTMLElement
  private elHpText!: HTMLElement
  private elGold!: HTMLElement
  private elKills!: HTMLElement
  private elCurse!: HTMLElement
  private elWeapons!: HTMLElement
  private elItems!: HTMLElement
  private elTomes!: HTMLElement
  private elDash!: HTMLElement
  private elFps!: HTMLElement
  private elBossBar!: HTMLElement
  private elBossName!: HTMLElement
  private elBossFill!: HTMLElement
  private minimap!: HTMLCanvasElement
  private minimapCtx!: CanvasRenderingContext2D
  private elObjective!: HTMLElement
  private elFlash!: HTMLDivElement
  private elCombo!: HTMLElement
  private elVignette!: HTMLDivElement
  private elWorldVignette!: HTMLDivElement
  private elHurt!: HTMLDivElement
  private elBossCard!: HTMLDivElement
  private chestSpin: number | null = null
  private floaterNodes = new Map<number, HTMLElement>() // pooled damage-floater DOM keyed by id
  private floaterSeen = new Set<number>()
  private elEdge!: HTMLDivElement
  private edgeNodes = new Map<string, HTMLElement>() // pooled offscreen-indicator chevrons
  private edgeSeen = new Set<string>()
  private evoReadySet = new Set<string>() // weapon defIds already flagged "ready to evolve" (toast once)

  constructor(root: HTMLElement) {
    this.hud = document.createElement('div')
    this.hud.id = 'hud'
    this.hud.innerHTML = `
      <div class="hud-top">
        <div class="hud-timer">0:00</div>
        <div style="position:relative;width:100%">
          <div class="hud-level">LV 1</div>
          <div class="hud-xpbar"><div class="hud-xpfill"></div></div>
        </div>
      </div>
      <div class="hud-vitals">
        <div class="hud-hpbar"><div class="hud-hpfill"></div><div class="hud-hptext">100/100</div></div>
        <div class="hud-stats">
          <span class="gold">${chromeIconHTML('gold')} <span class="v-gold">0</span></span>
          <span>${chromeIconHTML('kills')} <span class="v-kills">0</span></span>
          <span class="v-curse" style="color:#ff2e97"></span>
        </div>
      </div>
      <div class="hud-loadout">
        <div class="hud-row v-weapons"></div>
        <div class="hud-row v-items"></div>
        <div class="hud-row v-tomes"></div>
      </div>
      <div class="hud-dash">⎵ DASH</div>
      <div class="hud-pad" title="${t('gamepadConnected')}">${chromeIconHTML('gamepad', 22)}</div>
      <div class="hud-combo"></div>
      <div class="fps"></div>
      <div class="boss-bar"><div class="boss-name">BOSS</div><div class="boss-hpwrap"><div class="boss-hpfill"></div></div></div>
      <div class="objective"></div>
      <canvas class="minimap" width="168" height="168"></canvas>
    `
    root.appendChild(this.hud)

    this.floaters = document.createElement('div')
    this.floaters.id = 'floaters'
    root.appendChild(this.floaters)

    this.toastHost = document.createElement('div')
    this.toastHost.id = 'toast-host'
    root.appendChild(this.toastHost)

    this.overlay = document.createElement('div')
    this.overlay.id = 'overlay'
    root.appendChild(this.overlay)

    this.elFlash = document.createElement('div')
    this.elFlash.className = 'screen-flash'
    root.appendChild(this.elFlash)

    this.elWorldVignette = document.createElement('div')
    this.elWorldVignette.id = 'world-vignette'
    root.appendChild(this.elWorldVignette)

    this.elVignette = document.createElement('div')
    this.elVignette.id = 'danger-vignette'
    root.appendChild(this.elVignette)

    // per-HIT red flash — pops on every point of HP damage so getting hit is unmistakable (user feedback)
    this.elHurt = document.createElement('div')
    this.elHurt.id = 'hurt-flash'
    root.appendChild(this.elHurt)

    this.elBossCard = document.createElement('div')
    this.elBossCard.id = 'boss-card'
    root.appendChild(this.elBossCard)

    this.elEdge = document.createElement('div')
    this.elEdge.id = 'edge-indicators'
    root.appendChild(this.elEdge)

    this.elTimer = this.hud.querySelector('.hud-timer')!
    this.elXp = this.hud.querySelector('.hud-xpfill')!
    this.elLevel = this.hud.querySelector('.hud-level')!
    this.elHp = this.hud.querySelector('.hud-hpfill')!
    this.elHpText = this.hud.querySelector('.hud-hptext')!
    this.elGold = this.hud.querySelector('.v-gold')!
    this.elKills = this.hud.querySelector('.v-kills')!
    this.elCurse = this.hud.querySelector('.v-curse')!
    this.elWeapons = this.hud.querySelector('.v-weapons')!
    this.elItems = this.hud.querySelector('.v-items')!
    this.elTomes = this.hud.querySelector('.v-tomes')!
    this.elDash = this.hud.querySelector('.hud-dash')!
    this.elCombo = this.hud.querySelector('.hud-combo')!
    this.elFps = this.hud.querySelector('.fps')!
    this.elBossBar = this.hud.querySelector('.boss-bar')!
    this.elBossName = this.hud.querySelector('.boss-name')!
    this.elBossFill = this.hud.querySelector('.boss-hpfill')!
    this.minimap = this.hud.querySelector('.minimap') as HTMLCanvasElement
    this.minimapCtx = this.minimap.getContext('2d')!
    this.elObjective = this.hud.querySelector('.objective')!

    this.hideHud()
  }

  showHud(): void {
    this.hud.style.display = 'block'
  }
  hideHud(): void {
    this.hud.style.display = 'none'
    for (const node of this.edgeNodes.values()) node.remove()
    this.edgeNodes.clear()
    // also reap floater DOM (syncFloaters stops running once the run ends) so damage numbers don't leak across runs
    for (const node of this.floaterNodes.values()) node.remove()
    this.floaterNodes.clear()
  }

  updateHud(world: World, fps: number, camYaw = 0): void {
    const p = world.player
    this.elTimer.textContent = fmtTime(world.time)
    this.elLevel.textContent = `LV ${p.level}`
    this.elXp.style.width = `${Math.min(100, (p.xp / p.xpToNext) * 100)}%`
    const hpPct = Math.max(0, (p.hp / world.stats.maxHp) * 100)
    this.elHp.style.width = `${hpPct}%`
    this.elHpText.textContent = `${Math.ceil(p.hp)}/${Math.round(world.stats.maxHp)}`

    // screen vignettes: a dark edge-darken that rises with swarm pressure + dusk, plus the red
    // low-HP danger ring (invisible above 35% HP, ramps toward death)
    const frac = hpPct / 100
    const lowHp = frac < 0.35 ? (0.35 - frac) / 0.35 : 0
    const threat = Math.min(1, world.enemies.length / 110)
    const cool = dayLight(world.time).cool
    const vig = vignetteLevels(threat, cool, lowHp)
    this.elWorldVignette.style.opacity = vig.dark.toFixed(2)
    this.elVignette.style.opacity = vig.danger.toFixed(2)
    this.elVignette.classList.toggle('critical', frac < 0.18)
    // per-hit red flash: p.flash is set to 0.15 the instant HP is lost, then decays — drives a sharp red edge-pulse so
    // taking a hit is always readable, even mid-swarm. Stronger when low on HP.
    const hurt = Math.min(0.7, (p.flash / 0.15) * (0.42 + 0.3 * lowHp))
    this.elHurt.style.opacity = hurt.toFixed(2)

    // kill-combo counter (rising crunch feedback)
    if (world.combo >= 5) {
      this.elCombo.textContent = `${world.combo}× COMBO`
      this.elCombo.classList.add('show')
      this.elCombo.classList.toggle('hot', world.combo >= 50)
    } else {
      this.elCombo.classList.remove('show')
    }
    this.elGold.textContent = `${world.gold}`
    this.elKills.textContent = `${world.kills}`
    this.elCurse.innerHTML = world.curse > 0 ? `${chromeIconHTML('curse')} ${world.curse}` : ''
    this.elDash.className = p.dashCd <= 0 ? 'hud-dash ready' : 'hud-dash'
    this.elDash.textContent = this.padActive ? t('dashPad') : t('dash')
    // recharge readout: a fill sweeps left→right behind the label as the dash/slide cools down (was binary ready/not)
    this.elDash.style.setProperty('--dash', (Math.max(0, Math.min(1, 1 - p.dashCd / DASH_CD))).toFixed(3))

    // loadout — weapons, with an evolution-readiness cue (#missed4)
    let wh = ''
    const readyNow = new Set<string>()
    for (const ws of world.weapons) {
      const def = WEAPONS[ws.defId]
      if (!def) continue
      let cls = ws.evolved ? 'evolved' : ''
      let badge = ''
      let title = tName('weapon', def.id, def.name)
      // surface the evolve path: glowing badge when ready, a hint + recipe otherwise
      if (!ws.evolved && def.evolvesTo && def.evolveItem) {
        const evoTarget = WEAPONS[def.evolvesTo]
        const catDef = ITEMS[def.evolveItem]
        const evoName = evoTarget ? tName('weapon', def.evolvesTo, evoTarget.name) : def.evolvesTo
        const catName = catDef ? tName('item', def.evolveItem, catDef.name) : def.evolveItem
        const catIcon = iconHTML(itemIconSprite(def.evolveItem), { size: 16, fallback: '' })
        const atMax = ws.level >= def.maxLevel
        const hasCat = world.hasItem(def.evolveItem)
        if (atMax && hasCat) {
          cls += ' evo-ready'
          badge = `<span class="evo-badge">${chromeIconHTML('evolve', 16)}</span>`
          title = `${t('evoReady')} → ${evoName}`
          readyNow.add(def.id)
        } else if (atMax || hasCat) {
          // close to ready — nudge with the missing piece + full recipe
          cls += ' evo-path'
          badge = atMax
            ? `<span class="evo-hint">${catIcon}</span>` // has max, still needs the catalyst
            : `<span class="evo-hint dim">${chromeIconHTML('evolve', 16)}</span>` // has catalyst, needs to reach max level
          const need = atMax ? `${t('evoNeedsItem')} ${catName}` : t('evoNeedsMax')
          title = `→ ${evoName} · ${need}`
        } else {
          title = `→ ${evoName} (${tName('weapon', def.id, def.name)} + ${catName})`
        }
      }
      wh += `<div class="slot cat-weapon ${cls}" title="${title}">${iconHTML(weaponIconSprite(def.id), { size: 30, fallback: def.icon })}<span class="lv">${ws.level}</span>${badge}</div>`
    }
    const capBadge = (full: boolean) => (full ? `<span class="cap-full" title="${t('cap_full_tip')}">${t('cap_full')}</span>` : '')
    this.elWeapons.innerHTML = wh + capBadge(world.weapons.length >= MAX_WEAPONS)
    // toast once when a weapon first becomes evolve-ready
    for (const id of readyNow) {
      if (!this.evoReadySet.has(id)) {
        const evo = WEAPONS[WEAPONS[id]?.evolvesTo ?? '']
        const evoName = evo ? tName('weapon', evo.id, evo.name) : ''
        this.toast(`${t('evoReadyToast')}: ${evoName}`)
      }
    }
    this.evoReadySet = readyNow
    let ih = ''
    for (const it of world.items) {
      const def = ITEMS[it.defId]
      if (!def) continue
      ih += `<div class="slot cat-item" title="${tName('item', def.id, def.name)}">${iconHTML(itemIconSprite(def.id), { size: 30, fallback: def.icon })}<span class="lv">${it.level}</span></div>`
    }
    this.elItems.innerHTML = ih + capBadge(world.items.length >= MAX_ITEMS)
    let th = ''
    for (const tm of world.tomes) {
      const def = TOMES[tm.defId]
      if (!def) continue
      th += `<div class="slot cat-tome" title="${tName('tome', def.id, def.name)}">${iconHTML(tomeIconSprite(def.id), { size: 30, fallback: def.icon })}<span class="lv">${tm.level}</span></div>`
    }
    this.elTomes.innerHTML = th + capBadge(world.tomes.length >= MAX_TOMES)

    this.elFps.textContent = `${Math.round(fps)} fps · ${world.enemies.length} ${t('foes')}`

    // boss / mini-boss health bar (real bosses take priority over champions)
    let boss = null
    let mini = null
    for (const e of world.enemies) {
      if (!e.alive) continue
      if (e.isBoss) { boss = e; break }
      if (e.isMiniBoss && !mini) mini = e
    }
    const bar = boss ?? mini
    if (bar) {
      this.elBossBar.style.display = 'block'
      this.elBossBar.classList.toggle('mini', !boss)
      const cat = boss ? 'boss' : 'enemy'
      const label = tName(cat, bar.defId, ENEMIES[bar.defId]?.name ?? 'BOSS').toUpperCase()
      this.elBossName.textContent = boss ? label : `★ ${label}`
      this.elBossFill.style.width = `${Math.max(0, (bar.hp / bar.maxHp) * 100)}%`
    } else {
      this.elBossBar.style.display = 'none'
    }

    // objective tracker
    if (world.objective === 'endless') {
      this.elObjective.textContent = t('objEndless')
      this.elObjective.style.color = '#ff2e97'
    } else if (world.objective === 'portal') {
      const ch = world.portal ? Math.round(world.portal.channel * 100) : 0
      this.elObjective.textContent =
        world.portal && world.portal.channel > 0 ? t('obj_portal_channel', ch) : t('obj_portal')
      this.elObjective.style.color = '#1fe3d0'
    } else if (world.objective === 'finalboss') {
      this.elObjective.textContent = t('obj_finalboss')
      this.elObjective.style.color = '#ff6a7a'
    } else if (world.stageGoal === 'elite-hunt') {
      this.elObjective.textContent = `${t('obj_elitehunt')} · ${Math.min(world.goalProgress, world.goalTarget)} / ${world.goalTarget}`
      this.elObjective.style.color = '#ffd23d'
    } else if (world.stageGoal === 'slay') {
      this.elObjective.textContent = `${t('obj_slay')} · ${Math.min(world.goalProgress, world.goalTarget)} / ${world.goalTarget}`
      this.elObjective.style.color = '#ff9a3d'
    } else {
      this.elObjective.textContent = `${t('obj_survive')} · ${fmtTime(Math.min(world.time, world.portalAt))} / ${fmtTime(world.portalAt)}`
      this.elObjective.style.color = ''
    }

    this.drawMinimap(world, camYaw)
  }

  private reliefCv: HTMLCanvasElement | null = null
  private reliefCtx: CanvasRenderingContext2D | null = null
  private reliefPx = 0
  private reliefPz = 0
  private reliefTick = 0
  private reliefMapId = ''

  private drawMinimap(world: World, camYaw = 0): void {
    const ctx = this.minimapCtx
    const W = this.minimap.width
    const C = W / 2
    const p = world.player
    const view = 42 // world radius shown
    const sc = C / view
    ctx.clearRect(0, 0, W, W)
    // clip to circle
    ctx.save()
    ctx.beginPath()
    ctx.arc(C, C, C - 1, 0, Math.PI * 2)
    ctx.clip()
    // rotate the map with the free-orbit camera so "up" on the minimap is always camera-forward (the relief +
    // every blip rotate together; the player stays pinned at center). Matches the edge-indicator transform.
    ctx.translate(C, C)
    ctx.rotate(camYaw)
    ctx.translate(-C, -C)

    // terrain relief backdrop — the minimap mirrors the real 3D world (hills bright, basins
    // and cliffs dark), tinted with the biome palette, so it reads as this place from above.
    const amp = world.terrainAmp
    if (amp > 0) {
      // The relief is heavy value-noise (~345 cells × 5 terrain samples). It only depends on (seed,amp,cfg) and the
      // player's sample window, so render it to an offscreen canvas a few times/sec and blit it (scrolled by the
      // player's movement) every frame, instead of recomputing ~27k noise ops per frame for a faint backdrop.
      if (!this.reliefCv) {
        this.reliefCv = document.createElement('canvas')
        this.reliefCv.width = W
        this.reliefCv.height = W
        this.reliefCtx = this.reliefCv.getContext('2d')
      }
      const rctx = this.reliefCtx
      this.reliefTick--
      if (rctx && (this.reliefMapId !== world.map.id || this.reliefTick <= 0)) {
        this.reliefTick = 4 // refresh cadence in frames — backdrop only, so the small lag is imperceptible
        this.reliefMapId = world.map.id
        this.reliefPx = p.x
        this.reliefPz = p.z
        const seed = world.terrainSeed
        const cfg = world.terrainCfg
        const m = world.map
        const gr = (m.groundColor >> 16) & 255, gg = (m.groundColor >> 8) & 255, gb = m.groundColor & 255
        const ar = (m.accentColor >> 16) & 255, ag = (m.accentColor >> 8) & 255, ab = m.accentColor & 255
        const fr = (m.fogColor >> 16) & 255, fg = (m.fogColor >> 8) & 255, fb = m.fogColor & 255
        const cell = 8
        rctx.clearRect(0, 0, W, W)
        for (let py = 0; py < W; py += cell) {
          for (let px = 0; px < W; px += cell) {
            const cxp = px + cell / 2 - C
            const cyp = py + cell / 2 - C
            if (cxp * cxp + cyp * cyp > C * C) continue // skip cells outside the dial
            const h = terrainHeight(p.x + cxp / sc, p.z + cyp / sc, seed, amp, cfg)
            const slope = terrainSlope(p.x + cxp / sc, p.z + cyp / sc, seed, amp, cfg)
            const hn = h / amp
            const shade = reliefShade(hn, Math.max(0, 1 - slope * 0.8))
            let r = gr * shade
            let g = gg * shade
            let b = gb * shade
            const peakT = Math.max(0, hn) * 0.22 * Math.max(0, 1 - slope) // high flat ground catches accent
            r += (ar - r) * peakT
            g += (ag - g) * peakT
            b += (ab - b) * peakT
            const basinT = Math.max(0, -hn) * 0.35 // basins sink toward fog colour
            r += (fr - r) * basinT
            g += (fg - g) * basinT
            b += (fb - b) * basinT
            rctx.fillStyle = `rgb(${r | 0},${g | 0},${b | 0})`
            rctx.fillRect(px, py, cell, cell)
          }
        }
      }
      // blit the cached relief, scrolled so it tracks the player's movement since the last recompute
      ctx.drawImage(this.reliefCv, -(p.x - this.reliefPx) * sc, -(p.z - this.reliefPz) * sc)
    }

    const plot = (wx: number, wz: number) => ({ x: C + (wx - p.x) * sc, y: C + (wz - p.z) * sc })
    const dot = (wx: number, wz: number, color: string, size: number, edge = false) => {
      let { x, y } = plot(wx, wz)
      const dx = x - C
      const dy = y - C
      const d = Math.hypot(dx, dy)
      if (d > C - 3) {
        if (!edge) return
        x = C + (dx / d) * (C - 4)
        y = C + (dy / d) * (C - 4)
      }
      ctx.fillStyle = color
      ctx.fillRect(x - size, y - size, size * 2, size * 2)
    }

    for (const e of world.enemies) {
      if (!e.alive) continue
      if (e.isBoss) dot(e.x, e.z, '#ff4060', 4, true)
      else if (e.isElite) dot(e.x, e.z, '#ffd23d', 2.4)
      else dot(e.x, e.z, '#ff8a8a', 1.3)
    }
    for (const pk of world.pickups) {
      if (!pk.alive) continue
      if (pk.kind === 'chest') dot(pk.x, pk.z, '#ffcc33', 2.6, true)
      else if (pk.kind === 'vendor') dot(pk.x, pk.z, '#66ff88', 3, true)
    }
    for (const s of world.shrines) {
      if (!s.used) dot(s.x, s.z, '#b46cff', 3, true)
    }
    if (world.portal && !world.portal.active) dot(world.portal.x, world.portal.z, '#1fe3d0', 3.4, true)
    ctx.restore()
    // player at center
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(C - 2.5, C - 2.5, 5, 5)
  }

  syncFloaters(world: World, renderer: Renderer, enabled = true): void {
    if (!enabled) {
      if (this.floaterNodes.size) {
        for (const node of this.floaterNodes.values()) node.remove()
        this.floaterNodes.clear()
      }
      return
    }
    // pooled: create a DOM node per floater id once, update it in place, remove on death
    const seen = this.floaterSeen
    seen.clear()
    for (const f of world.floaters) {
      const sp = renderer.worldToScreen(f.x, f.y, f.z)
      if (!sp.visible) continue
      seen.add(f.id)
      let node = this.floaterNodes.get(f.id)
      if (!node) {
        node = document.createElement('div')
        const num = parseInt(f.text, 10)
        // pure damage numbers get magnitude tiering + size; everything else keeps its semantic color
        if (!Number.isNaN(num) && (f.color === 0xffffff || f.color === 0xffd23d)) {
          const tier = num >= 500 ? 't-m' : num >= 150 ? 't-a' : num >= 40 ? 't-c' : 't-w'
          node.className = `floater ${tier}${f.crit ? ' crit' : ''}`
          const size = Math.min(58, 16 + Math.log2(Math.max(2, num)) * 3) * (f.crit ? 1.4 : 1)
          node.style.fontSize = `${size.toFixed(0)}px`
        } else {
          node.className = `floater${f.crit ? ' crit' : ''}`
          node.style.color = '#' + f.color.toString(16).padStart(6, '0')
        }
        node.textContent = f.text
        this.floaters.appendChild(node)
        this.floaterNodes.set(f.id, node)
      }
      node.style.left = `${sp.x}px`
      node.style.top = `${sp.y}px`
      node.style.opacity = String(Math.max(0, Math.min(1, f.life / f.maxLife)))
    }
    // reap floaters that have died/expired
    for (const [id, node] of this.floaterNodes) {
      if (!seen.has(id)) {
        node.remove()
        this.floaterNodes.delete(id)
      }
    }
  }

  /** Screen-edge chevrons pointing at important offscreen targets (boss/vendor/shrine/portal/chest). */
  syncEdgeIndicators(world: World, renderer: Renderer): void {
    const p = world.player
    const targets: { id: string; x: number; z: number; color: string }[] = []
    for (const e of world.enemies) {
      if (!e.alive) continue
      if (e.isBoss) targets.push({ id: 'boss' + e.id, x: e.x, z: e.z, color: '#ff4060' })
      else if (e.isMiniBoss) targets.push({ id: 'mini' + e.id, x: e.x, z: e.z, color: '#ff7a2d' })
    }
    for (const pk of world.pickups) if (pk.alive && pk.kind === 'vendor') targets.push({ id: 'vend' + pk.id, x: pk.x, z: pk.z, color: '#66ff88' })
    for (const s of world.shrines) if (!s.used) targets.push({ id: 'shr' + s.id, x: s.x, z: s.z, color: '#b46cff' })
    if (world.portal && !world.portal.active) targets.push({ id: 'portal', x: world.portal.x, z: world.portal.z, color: '#1fe3d0' })
    // directional-tide front: a chevron toward the side the surge is rolling in from
    if (world.surgePhase !== 'idle') {
      targets.push({ id: 'tide', x: p.x + Math.cos(world.tideAngle) * 40, z: p.z + Math.sin(world.tideAngle) * 40, color: '#ff8a3d' })
    }
    // nearest chest only (avoid clutter)
    let chest: { x: number; z: number } | null = null
    let cd = Infinity
    for (const pk of world.pickups) {
      if (!pk.alive || pk.kind !== 'chest') continue
      const d = (pk.x - p.x) ** 2 + (pk.z - p.z) ** 2
      if (d < cd) {
        cd = d
        chest = pk
      }
    }
    if (chest) targets.push({ id: 'chest', x: chest.x, z: chest.z, color: '#ffcc33' })

    const seen = this.edgeSeen
    seen.clear()
    const W = innerWidth
    const H = innerHeight
    const cx = W / 2
    const cy = H / 2
    const margin = 54
    for (const t of targets) {
      const sp = renderer.worldToScreen(t.x, 0, t.z)
      const onScreen = sp.visible && sp.x > margin && sp.x < W - margin && sp.y > margin && sp.y < H - margin
      if (onScreen) continue
      seen.add(t.id)
      // screen-space direction from the player toward the target, rotated by the camera's free-orbit yaw — the
      // INVERSE of rotateIntent's screen→world map (rotateIntent(·, yaw) ⇒ world, so world→screen = ·,-yaw) — so
      // the chevron points the right way once the camera is turned (else it lies, sending the player away from it)
      const sdir = rotateIntent(t.x - p.x, t.z - p.z, -renderer.camYaw)
      const ang = Math.atan2(sdir.z, sdir.x)
      const dx = Math.cos(ang)
      const dy = Math.sin(ang)
      const halfW = cx - margin
      const halfH = cy - margin
      const scale = Math.min(halfW / (Math.abs(dx) || 1e-3), halfH / (Math.abs(dy) || 1e-3))
      const ex = cx + dx * scale
      const ey = cy + dy * scale
      let node = this.edgeNodes.get(t.id)
      if (!node) {
        node = document.createElement('div')
        node.className = 'edge-ind'
        node.textContent = '➤'
        this.elEdge.appendChild(node)
        this.edgeNodes.set(t.id, node)
      }
      node.style.left = `${ex}px`
      node.style.top = `${ey}px`
      node.style.color = t.color
      node.style.transform = `translate(-50%, -50%) rotate(${ang}rad)`
    }
    for (const [id, node] of this.edgeNodes) {
      if (!seen.has(id)) {
        node.remove()
        this.edgeNodes.delete(id)
      }
    }
  }

  flashScreen(color = '#ffffff', strength = 0.7): void {
    this.elFlash.style.background = color
    this.elFlash.style.transition = 'none'
    this.elFlash.style.opacity = String(strength)
    // next frame, fade out
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.elFlash.style.transition = 'opacity .5s ease-out'
        this.elFlash.style.opacity = '0'
      }),
    )
  }

  /** Cinematic full-width boss name-card: slams in, holds, then fades (CSS-driven). */
  bossIntro(name: string, title = ''): void {
    this.elBossCard.innerHTML = `<div class="boss-card-name">${name}</div>${title ? `<div class="boss-card-title">${title}</div>` : ''}`
    this.elBossCard.classList.remove('show')
    void this.elBossCard.offsetWidth // force reflow so the animation restarts
    this.elBossCard.classList.add('show')
    window.setTimeout(() => this.elBossCard.classList.remove('show'), 2600)
  }

  toast(text: string): void {
    const t = document.createElement('div')
    t.className = 'toast'
    t.textContent = text
    this.toastHost.appendChild(t)
    setTimeout(() => {
      t.style.transition = 'opacity .4s'
      t.style.opacity = '0'
      setTimeout(() => t.remove(), 400)
    }, 1800)
  }

  private show(html: string): void {
    this.overlay.innerHTML = html
    this.overlay.classList.add('show')
    this.focusIdx = -1
    if (this.padActive) this.resetFocus() // auto-focus the first card/button for pad users
  }
  hideOverlay(): void {
    if (this.chestSpin) {
      clearTimeout(this.chestSpin)
      this.chestSpin = null
    }
    this.overlay.classList.remove('show')
    this.overlay.innerHTML = ''
    this.focusIdx = -1
  }

  // -------------------------------------------------------------------------
  // Gamepad / focus navigation of overlays
  // -------------------------------------------------------------------------
  private padActive = false
  private focusIdx = -1

  /** Reflect pad-connected state: shows pad glyph hints + arms overlay focus. */
  setPadActive(active: boolean): void {
    if (active === this.padActive) return
    this.padActive = active
    document.body.classList.toggle('pad-active', active)
    if (active && this.overlayOpen() && this.focusIdx < 0) this.resetFocus()
  }

  /** True when a navigable card/menu overlay is on screen. */
  overlayOpen(): boolean {
    return this.overlay.classList.contains('show') && this.navTargets().length > 0
  }

  /** Ordered list of focusable elements in the current overlay. */
  private navTargets(): HTMLElement[] {
    // scope pad focus to the topmost modal (confirm / install help) when one is open, so the gamepad can't
    // navigate onto the buttons of the screen behind it
    const root = this.overlay.querySelector<HTMLElement>('.install-help') ?? this.overlay
    return Array.from(
      root.querySelectorAll<HTMLElement>('.lvcard, .card:not(.locked), .btn:not([disabled]), #chest-take:not([disabled]), #chest-gamble:not([disabled])'),
    )
  }

  private applyFocus(targets: HTMLElement[]): void {
    targets.forEach((el, i) => el.classList.toggle('gp-focus', i === this.focusIdx))
    const el = targets[this.focusIdx]
    if (el) el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }

  private resetFocus(): void {
    const targets = this.navTargets()
    if (targets.length === 0) {
      this.focusIdx = -1
      return
    }
    this.focusIdx = 0
    this.applyFocus(targets)
  }

  moveFocus(dir: 'up' | 'down' | 'left' | 'right'): void {
    const targets = this.navTargets()
    if (targets.length === 0) return
    if (this.focusIdx < 0) {
      this.resetFocus()
      return
    }
    const delta = dir === 'left' || dir === 'up' ? -1 : 1
    this.focusIdx = (this.focusIdx + delta + targets.length) % targets.length
    this.applyFocus(targets)
  }

  activateFocus(): void {
    const targets = this.navTargets()
    if (targets.length === 0) return
    if (this.focusIdx < 0) {
      this.resetFocus()
      return
    }
    targets[this.focusIdx]?.click()
  }

  /** B / back: trigger the overlay's back/close/skip/resume affordance if present. */
  navBack(): void {
    // a topmost modal (confirm/install help) gets priority: B closes it instead of the screen behind it
    const modal = this.overlay.querySelector<HTMLElement>('.install-help')
    if (modal) { modal.querySelector<HTMLElement>('[data-act=cancel], [data-act=ok]')?.click(); return }
    const back = this.overlay.querySelector<HTMLElement>(
      '[data-act=back], [data-act=close], [data-act=skip], [data-act=resume], [data-act=menu], [data-act=leave]',
    )
    back?.click()
  }

  // ---- Chest reveal (rarity-roulette) ----
  chestReveal(option: UpgradeOption, h: { take: () => void; gamble?: () => void }, gambleState?: { cost: number; canGamble: boolean }): void {
    const rarities = ['common', 'uncommon', 'rare', 'epic', 'legendary']
    // CO2: the reel cycles real pixel-art loot sprites (weapons + items), not emoji
    const spinPool = [
      ...WEAPON_LIST.filter((w) => !w.hidden).map((w) => weaponIconSprite(w.id)),
      ...ITEM_LIST.map((it) => itemIconSprite(it.id)),
    ]
    const gambleBtn = gambleState ? `<button class="btn" id="chest-gamble" disabled>${t('chestGamble', gambleState.cost)} ${chromeIconHTML('gold', 16)}</button>` : ''
    this.show(`
      <div class="panel" style="text-align:center;max-width:440px">
        <div class="h2">${t('chestTitle')}</div>
        <div class="lvcard rarity-common" id="chest-card" style="margin:18px auto;width:250px">
          <div class="ico" id="chest-ico">${iconHTML(spinPool[0], { size: 56 })}</div>
          <div class="name" id="chest-name">· · ·</div>
          <div class="desc" id="chest-desc"></div>
        </div>
        <div class="btn-row"><button class="btn primary" id="chest-take" disabled>${t('chestTake')}</button>${gambleBtn}</div>
      </div>
    `)
    const card = this.overlay.querySelector('#chest-card') as HTMLElement
    const ico = this.overlay.querySelector('#chest-ico') as HTMLElement
    const nameEl = this.overlay.querySelector('#chest-name') as HTMLElement
    const descEl = this.overlay.querySelector('#chest-desc') as HTMLElement
    const take = this.overlay.querySelector('#chest-take') as HTMLButtonElement
    if (this.chestSpin) clearTimeout(this.chestSpin)
    // slot-machine spin: fast at first, easing out to a stop, then the prize lands with a flourish
    const delays = chestSpinDelays(20)
    let ticks = 0
    const spinOnce = () => {
      if (ticks < delays.length) {
        card.className = `lvcard rarity-${rarities[Math.floor(Math.random() * rarities.length)]}`
        ico.innerHTML = iconHTML(spinPool[Math.floor(Math.random() * spinPool.length)], { size: 56 })
        this.chestSpin = window.setTimeout(spinOnce, delays[ticks])
        ticks++
      } else {
        this.chestSpin = null
        card.className = `lvcard rarity-${option.rarity} ${optionCatClass(option.kind)}`
        void card.offsetWidth // restart the landing flourish animation
        card.classList.add('chest-land')
        // category ribbon lands with the prize (only now — during the spin the rarity is random, so a category would lie)
        if (!card.querySelector('.cat-ribbon')) card.insertAdjacentHTML('afterbegin', catRibbon(option.kind))
        ico.innerHTML = optionIconHTML(option, 56)
        nameEl.textContent = option.name
        descEl.textContent = option.desc
        take.disabled = false
        const gb = this.overlay.querySelector('#chest-gamble') as HTMLButtonElement | null
        if (gb && gambleState?.canGamble) gb.disabled = false
      }
    }
    spinOnce()
    take.addEventListener('click', () => {
      if (this.chestSpin) {
        clearTimeout(this.chestSpin)
        this.chestSpin = null
      }
      h.take()
    })
    const gambleEl = this.overlay.querySelector('#chest-gamble') as HTMLButtonElement | null
    if (gambleEl && h.gamble) {
      gambleEl.addEventListener('click', () => {
        if (this.chestSpin) {
          clearTimeout(this.chestSpin)
          this.chestSpin = null
        }
        h.gamble!()
      })
    }
  }

  // ---- Main menu ----
  mainMenu(
    meta: MetaState,
    h: {
      play: () => void
      shop: () => void
      lang: () => void
      stats: () => void
      settings: () => void
      achievements: () => void
      codex: () => void
      challenges: () => void
    },
  ): void {
    this.show(`
      <div class="panel cockpit" style="text-align:center">
        <span class="ck-bracket tl"></span><span class="ck-bracket tr"></span><span class="ck-bracket bl"></span><span class="ck-bracket br"></span>
        <div class="ck-status"><span class="ck-led"></span> ${t('sysOnline')} &nbsp;·&nbsp; SECT WHK-7 &nbsp;·&nbsp; ${t('linkStable')} <span class="ck-led amber"></span></div>
        <div class="title">WHACKHALLA</div>
        <div class="subtitle">BONK YOUR WAY TO GLORY</div>
        <div class="ck-readout muted">${t('bank')} ${chromeIconHTML('gold')} ${meta.bank} &nbsp;·&nbsp; ${t('best')} ${fmtTime(meta.bestTime)} &nbsp;·&nbsp; ${t('runs')} ${meta.totalRuns}</div>
        <div class="btn-row">
          <button class="btn primary big" data-act="play">${chromeIconHTML('play', 22)} ${t('play')}</button>
          <button class="btn big" data-act="shop">${chromeIconHTML('store', 22)} ${t('shrine_btn')}</button>
        </div>
        ${isTouchDevice() && !isStandalone() ? `<div class="btn-row"><button class="btn primary big install-btn" data-act="install" style="flex:1 1 100%">${chromeIconHTML('install', 22)} ${t('installApp')}</button></div>` : ''}
        <div class="btn-row">
          <button class="btn" data-act="stats">${chromeIconHTML('stats')} ${t('stats_btn')}</button>
          <button class="btn" data-act="ach">${chromeIconHTML('achievements')} ${t('achievements_btn')}</button>
          <button class="btn" data-act="codex">${chromeIconHTML('codex')} ${t('codex_btn')}</button>
          <button class="btn" data-act="challenges">${chromeIconHTML('challenges')} ${t('challenges_btn')}</button>
          <button class="btn" data-act="settings">${chromeIconHTML('settings')} ${t('settings_btn')}</button>
          <button class="btn" data-act="lang">${chromeIconHTML('globe')} ${langLabel()}</button>
        </div>
        <div class="hint">${t('hint_menu')}</div>
      </div>
    `)
    const installBtn = this.overlay.querySelector('[data-act=install]')
    if (installBtn) installBtn.addEventListener('click', async () => {
      const r = await triggerInstall()
      if (r === 'accepted') installBtn.closest('.btn-row')?.remove() // hide once installed (no re-render needed)
      else if (r === 'ios' || r === 'manual') this.installHelp(r)
    })
    this.overlay.querySelector('[data-act=play]')!.addEventListener('click', h.play)
    this.overlay.querySelector('[data-act=shop]')!.addEventListener('click', h.shop)
    this.overlay.querySelector('[data-act=lang]')!.addEventListener('click', h.lang)
    this.overlay.querySelector('[data-act=stats]')!.addEventListener('click', h.stats)
    this.overlay.querySelector('[data-act=settings]')!.addEventListener('click', h.settings)
    this.overlay.querySelector('[data-act=ach]')!.addEventListener('click', h.achievements)
    this.overlay.querySelector('[data-act=codex]')!.addEventListener('click', h.codex)
    this.overlay.querySelector('[data-act=challenges]')!.addEventListener('click', h.challenges)
  }

  /** Modal with platform-specific steps when the native install prompt isn't available (iOS / other browsers). */
  private installHelp(kind: 'ios' | 'manual'): void {
    const steps = kind === 'ios' ? t('installIosSteps') : t('installManualSteps')
    const modal = document.createElement('div')
    modal.className = 'install-help'
    modal.innerHTML = `
      <div class="install-help-card">
        <div class="ico">${chromeIconHTML('install', 56)}</div>
        <div class="h2">${t('installTitle')}</div>
        <div class="muted">${steps}</div>
        <div class="btn-row"><button class="btn primary" data-act="ok">${t('close')}</button></div>
      </div>`
    this.overlay.appendChild(modal)
    const close = (): void => modal.remove()
    modal.querySelector('[data-act=ok]')!.addEventListener('click', close)
    modal.addEventListener('click', (e) => { if (e.target === modal) close() })
  }

  /** Generic confirm modal for destructive actions (e.g. Reset Save). */
  confirm(body: string, yesLabel: string, onYes: () => void): void {
    const modal = document.createElement('div')
    modal.className = 'install-help'
    modal.innerHTML = `
      <div class="install-help-card">
        <div class="h2">${t('confirmTitle')}</div>
        <div class="muted">${body}</div>
        <div class="btn-row">
          <button class="btn" data-act="cancel">${t('cancelBtn')}</button>
          <button class="btn primary" data-act="yes" style="border-color:#a01060">${yesLabel}</button>
        </div>
      </div>`
    this.overlay.appendChild(modal)
    const close = (): void => modal.remove()
    modal.querySelector('[data-act=cancel]')!.addEventListener('click', close)
    modal.querySelector('[data-act=yes]')!.addEventListener('click', () => { close(); onYes() })
    modal.addEventListener('click', (e) => { if (e.target === modal) close() })
  }

  // ---- Achievements ----
  achievementsScreen(meta: MetaState, h: { close: () => void }): void {
    const unlocked = new Set(meta.achievements)
    let cards = ''
    for (const a of ACHIEVEMENTS) {
      const got = unlocked.has(a.id)
      cards += `
        <div class="card ${got ? '' : 'locked'}">
          <div class="ico">${got ? iconHTML(achievementIconSprite(a.id), { size: 48, fallback: a.icon }) : chromeIconHTML('lock', 56)}</div>
          <div class="name ${got ? 'txt-legendary' : ''}">${got ? '✓ ' : ''}${tName('ach', a.id, a.name)}</div>
          <div class="desc">${tBlurb('ach', a.id, a.desc)}</div>
          <div class="foot">${chromeIconHTML('gold')} ${a.reward ?? 50}</div>
        </div>`
    }
    this.show(`
      <div class="panel">
        <div class="h2" style="text-align:center">${t('achievementsTitle')}</div>
        <div class="muted" style="text-align:center">${t('achProgress', unlocked.size, ACHIEVEMENTS.length)}</div>
        <div class="grid">${cards}</div>
        <div class="btn-row"><button class="btn primary" data-act="close">${t('close')}</button></div>
      </div>
    `)
    this.overlay.querySelector('[data-act=close]')!.addEventListener('click', h.close)
  }

  // ---- Content pool toggler (anti-dilution) — weapons, items AND tomes (ids are globally unique, so one set keys all) ----
  togglerScreen(meta: MetaState, h: { toggle: (id: string) => void; back: () => void }): void {
    const disabled = new Set(meta.disabledItems)
    const card = (id: string, cat: 'weapon' | 'item' | 'tome', name: string, icon: string, sprite: string) => {
      const off = disabled.has(id)
      return `
        <div class="card ${off ? 'item-off' : ''}" data-id="${id}">
          <div class="ico">${iconHTML(sprite, { size: 48, fallback: icon })}</div>
          <div class="name">${tName(cat, id, name)}</div>
          <div class="foot">${off ? chromeIconHTML('banish', 16) + ' ' + t('itemDisabled') : chromeIconHTML('check', 16) + ' ' + t('itemEnabled')}</div>
        </div>`
    }
    const weapons = WEAPON_LIST.filter((w) => !w.hidden).map((w) => card(w.id, 'weapon', w.name, w.icon, weaponIconSprite(w.id))).join('')
    const items = ITEM_LIST.map((it) => card(it.id, 'item', it.name, it.icon, itemIconSprite(it.id))).join('')
    const tomes = TOME_LIST.map((tm) => card(tm.id, 'tome', tm.name, tm.icon, tomeIconSprite(tm.id))).join('')
    this.show(`
      <div class="panel">
        <div class="h2">${t('togglerTitle')}</div>
        <div class="muted">${t('toggler_sub')}</div>
        <div class="h3">${t('codexWeapons')}</div><div class="grid">${weapons}</div>
        <div class="h3">${t('codexItems')}</div><div class="grid">${items}</div>
        <div class="h3">${t('codexTomes')}</div><div class="grid">${tomes}</div>
        <div class="btn-row"><button class="btn primary" data-act="back">${t('back')}</button></div>
      </div>
    `)
    this.overlay.querySelectorAll('.card').forEach((card) => {
      card.addEventListener('click', () => h.toggle((card as HTMLElement).dataset.id!))
    })
    this.overlay.querySelector('[data-act=back]')!.addEventListener('click', h.back)
  }

  // ---- Character select ----
  charSelect(meta: MetaState, h: { pick: (id: string) => void; back: () => void }): void {
    let cards = ''
    for (const c of CHARACTER_LIST) {
      const unlocked = meta.unlockedCharacters.includes(c.id)
      const w = WEAPONS[c.startWeapon]
      const wName = w ? tName('weapon', c.startWeapon, w.name) : ''
      cards += `
        <div class="card ${unlocked ? '' : 'locked'}" data-id="${c.id}" data-unlocked="${unlocked}">
          <div class="ico">${unlocked ? (characterIconHTML(c.color, 56) || iconHTML(heroSpriteName(c.id), { size: 56, tint: c.color, fallback: '' })) : chromeIconHTML('lock', 56)}</div>
          <div class="name">${unlocked ? tName('char', c.id, c.name) : '???'}</div>
          <div class="desc">${unlocked ? tBlurb('char', c.id, c.blurb) : tHint('char', c.id, c.unlockHint)}</div>
          ${!unlocked ? unlockBar(c.id, meta) : ''}
          ${unlocked && c.passiveDesc ? `<div class="desc txt-legendary">${chromeIconHTML('evolve', 16)} ${tPassive(c.id, c.passiveDesc)}</div>` : ''}
          <div class="foot">${unlocked ? `${t('startsWith')} ${iconHTML(weaponIconSprite(c.startWeapon), { size: 18, fallback: '' })} ${wName}` : ''}</div>
        </div>`
    }
    this.show(`
      <div class="panel">
        <div class="h2">${t('chooseChampion')}</div>
        <div class="muted">${t('champion_sub')}</div>
        <div class="grid">${cards}</div>
        <div class="btn-row"><button class="btn" data-act="back">${t('back')}</button></div>
      </div>
    `)
    this.overlay.querySelectorAll('.card').forEach((card) => {
      card.addEventListener('click', () => {
        if ((card as HTMLElement).dataset.unlocked === 'true') h.pick((card as HTMLElement).dataset.id!)
      })
    })
    this.overlay.querySelector('[data-act=back]')!.addEventListener('click', h.back)
  }

  // ---- Map select ----
  mapSelect(meta: MetaState, h: { pick: (id: string) => void; back: () => void }): void {
    let cards = ''
    for (const m of MAP_LIST) {
      const unlocked = meta.unlockedMaps.includes(m.id)
      const cleared = meta.clearedMaps.includes(m.id)
      const bt = meta.bestTier[m.id] ?? 0
      const bc = meta.bestCurse[m.id] ?? 0
      const records = unlocked && (bt > 0 || bc > 0) ? ` · ★${bt} · ${chromeIconHTML('curse', 16)}${bc}` : ''
      cards += `
        <div class="card ${unlocked ? '' : 'locked'}" data-id="${m.id}" data-unlocked="${unlocked}">
          <div class="ico">${unlocked ? iconHTML(mapIconSprite(m.id), { size: 56, fallback: '' }) : chromeIconHTML('lock', 56)}</div>
          <div class="name">${unlocked ? tName('map', m.id, m.name) : '???'} ${cleared ? chromeIconHTML('check', 16) : ''}</div>
          <div class="desc">${unlocked ? tBlurb('map', m.id, m.blurb) : tHint('map', m.id, m.unlockHint)}</div>
          <div class="foot">${t('intensity')} ${chromeIconHTML('flame', 16).repeat(Math.round(m.intensity))}${records}</div>
        </div>`
    }
    this.show(`
      <div class="panel">
        <div class="h2">${t('chooseBattlefield')}</div>
        <div class="muted">${t('battlefield_sub')}</div>
        <div class="grid">${cards}</div>
        <div class="btn-row"><button class="btn" data-act="back">${t('back')}</button></div>
      </div>
    `)
    this.overlay.querySelectorAll('.card').forEach((card) => {
      card.addEventListener('click', () => {
        if ((card as HTMLElement).dataset.unlocked === 'true') h.pick((card as HTMLElement).dataset.id!)
      })
    })
    this.overlay.querySelector('[data-act=back]')!.addEventListener('click', h.back)
  }

  // ---- Challenges ----
  challengesScreen(meta: MetaState, h: { pick: (id: string) => void; back: () => void }): void {
    const done = new Set(meta.challengesCompleted)
    let cards = ''
    for (const c of CHALLENGE_LIST) {
      const beaten = done.has(c.id)
      cards += `
        <div class="card" data-id="${c.id}">
          <div class="ico">${iconHTML(challengeIconSprite(c.id), { size: 48, fallback: c.icon })}</div>
          <div class="name txt-epic">${beaten ? '✓ ' : ''}${tName('challenge', c.id, c.name)}</div>
          <div class="desc">${tBlurb('challenge', c.id, c.desc)}</div>
          <div class="foot">${beaten ? t('challengeDone') : chromeIconHTML('gold') + ' 250'}</div>
        </div>`
    }
    this.show(`
      <div class="panel">
        <div class="h2">${t('challengesTitle')}</div>
        <div class="muted">${t('challenge_sub')}</div>
        <div class="grid">${cards}</div>
        <div class="btn-row"><button class="btn" data-act="back">${t('back')}</button></div>
      </div>
    `)
    this.overlay.querySelectorAll('.card').forEach((card) => {
      card.addEventListener('click', () => h.pick((card as HTMLElement).dataset.id!))
    })
    this.overlay.querySelector('[data-act=back]')!.addEventListener('click', h.back)
  }

  // ---- Difficulty select ----
  difficultySelect(h: { pick: (id: string) => void; back: () => void }): void {
    let cards = ''
    for (const d of DIFFICULTIES) {
      const col = '#' + d.color.toString(16).padStart(6, '0')
      cards += `
        <div class="card" data-id="${d.id}" style="border-color:${col}55">
          <div class="ico" style="color:${col}">◆</div>
          <div class="name" style="color:${col}">${tName('difficulty', d.id, d.name)}</div>
          <div class="desc">${tBlurb('difficulty', d.id, d.hint)}</div>
          <div class="foot">×${d.mult.toFixed(2)} ${t('foes')} · ×${d.rewardMult.toFixed(2)} ${chromeIconHTML('gold')}/${chromeIconHTML('evolve', 16)}</div>
        </div>`
    }
    this.show(`
      <div class="panel">
        <div class="h2">${t('chooseDifficulty')}</div>
        <div class="muted">${t('difficulty_sub')}</div>
        <div class="grid">${cards}</div>
        <div class="btn-row"><button class="btn" data-act="back">${t('back')}</button></div>
      </div>
    `)
    this.overlay.querySelectorAll('.card').forEach((card) => {
      card.addEventListener('click', () => h.pick((card as HTMLElement).dataset.id!))
    })
    this.overlay.querySelector('[data-act=back]')!.addEventListener('click', h.back)
  }

  tierSelect(mapId: string, maxTier: number, best: number, h: { pick: (tier: number) => void; back: () => void }): void {
    let cards = ''
    for (let tier = 1; tier <= maxTier; tier++) {
      const unlocked = tier <= best + 1
      const cleared = tier <= best
      const col = tier === 1 ? '#5fe6ff' : tier === 2 ? '#ffb13d' : '#ff4fa3'
      cards += `
        <div class="card ${unlocked ? '' : 'locked'}" data-tier="${tier}" data-unlocked="${unlocked}" style="border-color:${col}55">
          <div class="ico" style="color:${col}">${unlocked ? '★'.repeat(tier) : chromeIconHTML('lock', 56)}</div>
          <div class="name" style="color:${col}">${t('tier')} ${tier}</div>
          <div class="desc">${unlocked ? t('tierDesc', tier) : t('tierLocked')}</div>
          <div class="foot">${cleared ? '✓ ' + t('tierCleared') : ''}</div>
        </div>`
    }
    this.show(`
      <div class="panel">
        <div class="h2">${t('chooseTier')} · ${tName('map', mapId, MAPS[mapId]?.name ?? mapId)}</div>
        <div class="muted">${t('tier_sub')}</div>
        <div class="grid">${cards}</div>
        <div class="btn-row"><button class="btn" data-act="back">${t('back')}</button></div>
      </div>
    `)
    this.overlay.querySelectorAll('.card').forEach((card) => {
      const el = card as HTMLElement
      if (el.dataset.unlocked === 'false') return
      card.addEventListener('click', () => h.pick(parseInt(el.dataset.tier!, 10)))
    })
    this.overlay.querySelector('[data-act=back]')!.addEventListener('click', h.back)
  }

  // ---- Stats / records ----
  statsScreen(meta: MetaState, h: { close: () => void }): void {
    const row = (k: string, v: string | number) => `<div class="k">${k}</div><div class="v">${v}</div>`
    this.show(`
      <div class="panel" style="max-width:560px">
        <div class="h2" style="text-align:center">${t('statsTitle')}</div>
        <div class="summary-stats" style="max-width:520px">
          ${row(t('st_runs'), meta.totalRuns)}
          ${row(t('st_kills'), meta.totalKills)}
          ${row(t('st_bestTime'), fmtTime(meta.bestTime))}
          ${row(t('st_bestLevel'), meta.bestLevel)}
          ${row(t('st_bosses'), meta.bossesKilled)}
          ${row(t('st_gold'), chromeIconHTML('gold') + ' ' + meta.totalGoldEarned)}
          ${row(t('st_cleared'), `${meta.clearedMaps.length}/${MAP_LIST.length}`)}
          ${row(t('st_chars'), `${meta.unlockedCharacters.length}/${CHARACTER_LIST.length}`)}
        </div>
        <div class="btn-row"><button class="btn primary" data-act="close">${t('close')}</button></div>
      </div>
    `)
    this.overlay.querySelector('[data-act=close]')!.addEventListener('click', h.close)
  }

  // ---- Meta shop (Shrine) ----
  shop(meta: MetaState, h: { buy: (id: string) => void; back: () => void; reset: () => void; toggler: () => void }): void {
    let cards = ''
    for (const u of META_UPGRADES) {
      const lvl = meta.upgrades[u.id] ?? 0
      const cost = upgradeCost(u.id, lvl)
      const maxed = cost === null
      const afford = cost !== null && meta.bank >= cost
      cards += `
        <div class="card ${maxed || !afford ? 'locked' : ''}" data-id="${u.id}" data-buyable="${!maxed && afford}">
          <div class="ico">${iconHTML(metaIconSprite(u.id), { size: 48, fallback: u.icon })}</div>
          <div class="name">${tName('meta', u.id, u.name)} <span class="muted">${lvl}/${u.maxLevel}</span></div>
          <div class="desc">${tDesc('meta', u.id, lvl + (maxed ? 0 : 1), u.desc)}</div>
          <div class="foot">${maxed ? `<span class="maxed">${t('maxed')}</span>` : `<span class="shop-cost">${chromeIconHTML('gold')} ${cost}</span>`}</div>
        </div>`
    }
    this.show(`
      <div class="panel">
        <div class="h2">${t('shrineTitle')}</div>
        <div class="bank">${chromeIconHTML('gold')} ${meta.bank}</div>
        <div class="muted">${t('shrine_sub')}</div>
        <div class="grid">${cards}</div>
        <div class="btn-row">
          <button class="btn" data-act="back">${t('back')}</button>
          <button class="btn" data-act="toggler">${t('togglerBtn')}</button>
          <button class="btn" data-act="reset" style="opacity:.5">${t('resetSave')}</button>
        </div>
      </div>
    `)
    this.overlay.querySelectorAll('.card').forEach((card) => {
      card.addEventListener('click', () => {
        if ((card as HTMLElement).dataset.buyable === 'true') h.buy((card as HTMLElement).dataset.id!)
      })
    })
    this.overlay.querySelector('[data-act=back]')!.addEventListener('click', h.back)
    this.overlay.querySelector('[data-act=toggler]')!.addEventListener('click', h.toggler)
    this.overlay.querySelector('[data-act=reset]')!.addEventListener('click', h.reset)
  }

  // ---- Level up (with reroll / banish / lock) ----
  levelUp(options: UpgradeOption[], remaining: number, state: LevelUpState, h: LevelUpHandlers): void {
    let cards = ''
    for (let i = 0; i < options.length; i++) {
      const o = options[i]
      const locked = state.lockedKey === optionKey(o)
      const lockBtn = isLockable(o)
        ? `<button class="lvmini ${locked ? 'on' : ''}" data-act="lock" data-i="${i}" title="${t('lockCardTip')}">${chromeIconHTML('pin', 18)}</button>`
        : ''
      const banishBtn =
        isBanishable(o) && state.banishCharges > 0
          ? `<button class="lvmini" data-act="banish" data-i="${i}" title="${t('banishTip', state.banishCharges)}">${chromeIconHTML('banish', 18)}</button>`
          : ''
      cards += `
        <div class="lvcard rarity-${o.rarity} ${optionCatClass(o.kind)} ${locked ? 'on' : ''}" data-i="${i}">
          ${catRibbon(o.kind)}
          <div class="tag txt-${o.rarity}">${o.tag}</div>
          <div class="ico">${optionIconHTML(o, 48)}</div>
          <div class="name">${o.name}</div>
          <div class="desc">${o.desc}</div>
          <div class="lvactions">${lockBtn}${banishBtn}</div>
        </div>`
    }
    this.show(`
      <div class="panel" style="text-align:center;background:transparent;border:none;box-shadow:none">
        <div class="h2">${t('levelUp')}</div>
        <div class="muted">${remaining > 1 ? t('queued', remaining) : t('chooseUpgrade')}</div>
        <div class="levelup-grid" style="margin-top:18px">${cards}</div>
        <div class="lvfooter">
          <button class="btn" data-act="reroll" ${state.canReroll ? '' : 'disabled'}>${t('reroll', state.rerollCost)} ${chromeIconHTML('gold', 16)}</button>
          <button class="btn" data-act="skip">${t('skipUp')} ${chromeIconHTML('gold', 16)}</button>
          <span class="muted">${chromeIconHTML('banish', 16)} ${t('left', state.banishCharges)} &nbsp;·&nbsp; ${chromeIconHTML('gold')} ${state.gold}</span>
        </div>
      </div>
    `)
    this.overlay.querySelectorAll('.lvcard').forEach((card) => {
      card.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.lvactions')) return // mini-button click, not a pick
        const i = parseInt((card as HTMLElement).dataset.i!, 10)
        h.pick(options[i])
      })
    })
    this.overlay.querySelectorAll('[data-act=lock]').forEach((b) =>
      b.addEventListener('click', (e) => {
        e.stopPropagation()
        h.lock(options[parseInt((b as HTMLElement).dataset.i!, 10)])
      }),
    )
    this.overlay.querySelectorAll('[data-act=banish]').forEach((b) =>
      b.addEventListener('click', (e) => {
        e.stopPropagation()
        h.banish(options[parseInt((b as HTMLElement).dataset.i!, 10)])
      }),
    )
    const reroll = this.overlay.querySelector('[data-act=reroll]')
    if (reroll) reroll.addEventListener('click', () => h.reroll())
    const skip = this.overlay.querySelector('[data-act=skip]')
    if (skip) skip.addEventListener('click', () => h.skip())
  }

  // ---- Microwave: pick an owned item to DUPLICATE (a same-rarity item is sacrificed) — Megabonk-style ----
  microwavePicker(
    choices: { name: string; rarity: string; desc: string; tag: string; iconHtml: string }[],
    h: { pick: (i: number) => void; cancel: () => void },
  ): void {
    let cards = ''
    for (let i = 0; i < choices.length; i++) {
      const c = choices[i]
      cards += `
        <div class="lvcard rarity-${c.rarity}" data-i="${i}">
          <div class="tag txt-${c.rarity}">${c.tag}</div>
          <div class="ico">${c.iconHtml}</div>
          <div class="name">${c.name}</div>
          <div class="desc">${c.desc}</div>
        </div>`
    }
    this.show(`
      <div class="panel" style="text-align:center;background:transparent;border:none;box-shadow:none">
        <div class="h2">${chromeIconHTML('store', 22)} ${t('microwaveTitle')}</div>
        <div class="muted">${t('microwavePick')}</div>
        <div class="levelup-grid" style="margin-top:18px">${cards}</div>
        <div class="lvfooter"><button class="btn" data-act="cancel">${t('microwaveCancel')} ${chromeIconHTML('gold', 16)}</button></div>
      </div>
    `)
    this.overlay.querySelectorAll('.lvcard').forEach((card) =>
      card.addEventListener('click', () => h.pick(parseInt((card as HTMLElement).dataset.i!, 10))),
    )
    const cancel = this.overlay.querySelector('[data-act=cancel]')
    if (cancel) cancel.addEventListener('click', () => h.cancel())
  }

  // ---- Codex / Bestiary ----
  codexScreen(h: { close: () => void }): void {
    const section = (title: string, cards: string) =>
      `<div class="h2" style="margin-top:18px">${title}</div><div class="grid">${cards}</div>`
    let weapons = ''
    for (const w of WEAPON_LIST) {
      if (w.hidden) continue
      weapons += `<div class="card"><div class="ico">${iconHTML(weaponIconSprite(w.id), { size: 48, fallback: w.icon })}</div><div class="name txt-${w.rarity}">${tName('weapon', w.id, w.name)}</div><div class="desc">${tDesc('weapon', w.id, 1, w.describe)}</div></div>`
    }
    let items = ''
    for (const it of ITEM_LIST) {
      items += `<div class="card"><div class="ico">${iconHTML(itemIconSprite(it.id), { size: 48, fallback: it.icon })}</div><div class="name txt-${it.rarity}">${tName('item', it.id, it.name)}</div><div class="desc">${tDesc('item', it.id, 1, it.describe)}</div></div>`
    }
    let tomes = ''
    for (const tm of TOME_LIST) {
      tomes += `<div class="card"><div class="ico">${iconHTML(tomeIconSprite(tm.id), { size: 48, fallback: tm.icon })}</div><div class="name txt-${tm.rarity}">${tName('tome', tm.id, tm.name)}</div><div class="desc">${tDesc('tome', tm.id, 1, tm.describe)}</div></div>`
    }
    let enemies = ''
    for (const e of ENEMY_LIST) {
      if ((e.weight ?? 0) <= 0) continue
      const ico = iconHTML(`enemy:${e.id}`, { size: 48, fallback: '' })
      enemies += `<div class="card"><div class="ico">${ico}</div><div class="name">${tName('enemy', e.id, e.name)}</div><div class="desc">HP ${e.hp} · DMG ${e.damage}</div></div>`
    }
    let heroes = ''
    for (const c of CHARACTER_LIST) {
      const ico = characterIconHTML(c.color, 48) || iconHTML(heroSpriteName(c.id), { size: 48, tint: c.color, fallback: '' })
      heroes += `<div class="card"><div class="ico">${ico}</div><div class="name">${tName('char', c.id, c.name)}</div><div class="desc">${tBlurb('char', c.id, c.blurb)}</div></div>`
    }
    this.show(`
      <div class="panel">
        <div class="h2" style="text-align:center">${t('codexTitle')}</div>
        ${section(t('codexWeapons'), weapons)}
        ${section(t('codexItems'), items)}
        ${section(t('codexTomes'), tomes)}
        ${section(t('codexEnemies'), enemies)}
        ${section(t('codexHeroes'), heroes)}
        <div class="btn-row"><button class="btn primary" data-act="close">${t('close')}</button></div>
      </div>
    `)
    this.overlay.querySelector('[data-act=close]')!.addEventListener('click', h.close)
  }

  // ---- Settings ----
  settingsScreen(meta: MetaState, h: { close: () => void; apply: () => void; lang: () => void }): void {
    const s = meta.settings
    const slider = (key: string, label: string, val: number) =>
      `<div style="display:flex;align-items:center;gap:12px;margin:10px 0">
        <div style="width:160px;text-align:right;opacity:.8">${label}</div>
        <input type="range" min="0" max="100" value="${Math.round(val * 100)}" data-slider="${key}" style="flex:1;pointer-events:auto" />
        <div style="width:42px" data-val="${key}">${Math.round(val * 100)}</div>
      </div>`
    const toggle = (key: string, label: string, on: boolean) =>
      `<div style="display:flex;align-items:center;gap:12px;margin:10px 0">
        <div style="width:160px;text-align:right;opacity:.8">${label}</div>
        <button class="btn" data-toggle="${key}" style="flex:1">${on ? t('on') : t('off')}</button>
      </div>`
    this.show(`
      <div class="panel" style="max-width:560px">
        <div class="h2" style="text-align:center">${t('settingsTitle')}</div>
        ${slider('master', t('s_master'), s.master)}
        ${slider('sfx', t('s_sfx'), s.sfx)}
        ${slider('music', t('s_music'), s.music)}
        ${toggle('screenShake', t('s_shake'), s.screenShake)}
        ${toggle('damageNumbers', t('s_dmgnum'), s.damageNumbers)}
        <div style="display:flex;align-items:center;gap:12px;margin:10px 0">
          <div style="width:160px;text-align:right;opacity:.8">${t('s_lang')}</div>
          <button class="btn" data-act="lang" style="flex:1">${chromeIconHTML('globe')} ${langLabel()}</button>
        </div>
        <div class="btn-row"><button class="btn primary" data-act="close">${t('close')}</button></div>
      </div>
    `)
    this.overlay.querySelectorAll('input[data-slider]').forEach((inp) => {
      inp.addEventListener('input', () => {
        const el = inp as HTMLInputElement
        const key = el.dataset.slider as 'master' | 'sfx' | 'music'
        const v = parseInt(el.value, 10) / 100
        meta.settings[key] = v
        const valEl = this.overlay.querySelector(`[data-val="${key}"]`)
        if (valEl) valEl.textContent = String(Math.round(v * 100))
        h.apply()
      })
    })
    this.overlay.querySelectorAll('button[data-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = (btn as HTMLElement).dataset.toggle as 'screenShake' | 'damageNumbers'
        meta.settings[key] = !meta.settings[key]
        ;(btn as HTMLElement).textContent = meta.settings[key] ? t('on') : t('off')
        h.apply()
      })
    })
    this.overlay.querySelector('[data-act=lang]')!.addEventListener('click', h.lang)
    this.overlay.querySelector('[data-act=close]')!.addEventListener('click', h.close)
  }

  // ---- In-run shop (traveling vendor) ----
  shopInRun(
    offers: ShopOffer[],
    gold: number,
    h: { buy: (i: number) => void; leave: () => void; reroll: () => void; lock: (i: number) => void },
    rerollState: { cost: number; canReroll: boolean },
    caps: { weaponFull: boolean; itemFull: boolean; tomeFull?: boolean } = { weaponFull: false, itemFull: false },
  ): void {
    let cards = ''
    for (let i = 0; i < offers.length; i++) {
      const o = offers[i]
      // a 2nd new-weapon/new-item offer becomes un-buyable once the loadout hit its cap (buyShop hard-guards it),
      // so don't render it as a clickable card that silently does nothing
      const capped = (o.kind === 'weapon-new' && caps.weaponFull) || (o.kind === 'item-new' && caps.itemFull) || (o.kind === 'tome-new' && !!caps.tomeFull)
      const buyable = !o.sold && !capped && gold >= o.price
      const lockBtn = !o.fixed && !o.sold ? `<button class="lvmini ${o.locked ? 'on' : ''}" data-act="lock" data-i="${i}" title="${t('lockRerollTip')}">${chromeIconHTML('pin', 18)}</button>` : ''
      cards += `
        <div class="card ${buyable ? '' : 'locked'} ${optionCatClass(o.kind)} ${o.locked ? 'on' : ''}" data-i="${i}" data-buyable="${buyable}">
          ${catRibbon(o.kind)}
          <div class="ico">${o.kind ? optionIconHTML(o, 48) : o.icon}</div>
          <div class="name txt-${o.rarity}">${o.name}</div>
          <div class="desc">${o.desc}</div>
          <div class="foot">${o.sold ? `<span class="maxed">${t('sold')}</span>` : `<span class="shop-cost">${chromeIconHTML('gold')} ${o.price}</span>`}</div>
          <div class="lvactions">${lockBtn}</div>
        </div>`
    }
    this.show(`
      <div class="panel">
        <div class="h2">${chromeIconHTML('store', 22)} ${t('vendorTitle')}</div>
        <div class="bank">${chromeIconHTML('gold')} ${gold}</div>
        <div class="muted">${t('vendor_sub')}</div>
        <div class="grid">${cards}</div>
        <div class="btn-row">
          <button class="btn" data-act="reroll" ${rerollState.canReroll ? '' : 'disabled'}>${t('shopReroll', rerollState.cost)} ${chromeIconHTML('gold', 16)}</button>
          <button class="btn primary big" data-act="leave">${t('leave')}</button>
        </div>
      </div>
    `)
    this.overlay.querySelectorAll('.card').forEach((card) => {
      card.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.lvactions')) return
        if ((card as HTMLElement).dataset.buyable === 'true') h.buy(parseInt((card as HTMLElement).dataset.i!, 10))
      })
    })
    this.overlay.querySelectorAll('[data-act=lock]').forEach((b) =>
      b.addEventListener('click', (e) => {
        e.stopPropagation()
        h.lock(parseInt((b as HTMLElement).dataset.i!, 10))
      }),
    )
    const rr = this.overlay.querySelector('[data-act=reroll]')
    if (rr) rr.addEventListener('click', () => h.reroll())
    this.overlay.querySelector('[data-act=leave]')!.addEventListener('click', h.leave)
  }

  // ---- Pause ----
  pause(meta: MetaState, world: World, h: { resume: () => void; quit: () => void; toggleShake: () => void }): void {
    this.show(`
      <div class="panel" style="text-align:center;max-width:520px">
        <div class="h2">${t('paused')}</div>
        <div class="summary-stats">
          <div class="k">${t('time')}</div><div class="v">${fmtTime(world.time)}</div>
          <div class="k">${t('level')}</div><div class="v">${world.player.level}</div>
          <div class="k">${t('kills')}</div><div class="v">${world.kills}</div>
          <div class="k">${t('gold')}</div><div class="v">${world.gold}</div>
        </div>
        <div class="btn-row">
          <button class="btn primary" data-act="resume">${t('resume')}</button>
          <button class="btn" data-act="shake">${meta.settings.screenShake ? t('screenShakeOn') : t('screenShakeOff')}</button>
          <button class="btn" data-act="quit">${t('quitRun')}</button>
        </div>
      </div>
    `)
    this.overlay.querySelector('[data-act=resume]')!.addEventListener('click', h.resume)
    this.overlay.querySelector('[data-act=quit]')!.addEventListener('click', h.quit)
    this.overlay.querySelector('[data-act=shake]')!.addEventListener('click', h.toggleShake)
  }

  // ---- Victory choice (stage cleared: extract or go endless) ----
  victoryChoice(world: World, h: { extract: () => void; endless: () => void }): void {
    this.show(`
      <div class="panel" style="text-align:center;max-width:560px">
        <div class="title" style="font-size:40px">${t('victory')}</div>
        <div class="subtitle">${t('victoryChoiceSub')}</div>
        <div class="summary-stats">
          <div class="k">${t('survived')}</div><div class="v">${fmtTime(world.time)}</div>
          <div class="k">${t('levelReached')}</div><div class="v">${world.player.level}</div>
          <div class="k">${t('kills')}</div><div class="v">${world.kills}</div>
          <div class="k">${t('gold')}</div><div class="v" style="color:var(--accent)">${chromeIconHTML('gold')} ${world.gold}</div>
        </div>
        <div class="btn-row">
          <button class="btn primary" data-act="extract">${t('extractBtn')}</button>
          <button class="btn" data-act="endless">${t('endlessBtn')}</button>
        </div>
      </div>
    `)
    this.overlay.querySelector('[data-act=extract]')!.addEventListener('click', h.extract)
    this.overlay.querySelector('[data-act=endless]')!.addEventListener('click', h.endless)
  }

  // ---- End screen (death / win) ----
  endScreen(
    victory: boolean,
    world: World,
    goldEarned: number,
    unlocks: string[],
    h: { again: () => void; menu: () => void },
  ): void {
    const unlockHtml = unlocks.length
      ? `<div class="muted" style="color:var(--legendary);margin-top:8px">${t('unlockedLabel')} ${unlocks.join(', ')}</div>`
      : ''

    // --- final build: weapons (evolved highlighted) + items + tomes, as icon chips ---
    const chip = (icon: string, lvl: number, title: string, evolved = false) =>
      `<div class="recap-chip${evolved ? ' evolved' : ''}" title="${title}">${icon}<span class="lv">${lvl}</span></div>`
    const buildChips = [
      ...world.weapons.map((w) => {
        const d = WEAPONS[w.defId]
        return d ? chip(iconHTML(weaponIconSprite(d.id), { size: 30, fallback: d.icon }), w.level, tName('weapon', d.id, d.name), w.evolved) : ''
      }),
      ...world.items.map((it) => {
        const d = ITEMS[it.defId]
        return d ? chip(iconHTML(itemIconSprite(d.id), { size: 30, fallback: d.icon }), it.level, tName('item', d.id, d.name)) : ''
      }),
      ...world.tomes.map((tm) => {
        const d = TOMES[tm.defId]
        return d ? chip(iconHTML(tomeIconSprite(d.id), { size: 30, fallback: d.icon }), tm.level, tName('tome', d.id, d.name)) : ''
      }),
    ].join('')
    const buildHtml = buildChips
      ? `<div class="recap-h">${t('recapBuild')}</div><div class="recap-build">${buildChips}</div>`
      : ''

    // --- damage-by-weapon breakdown: ranked bars, % of total, MVP highlighted ---
    const dmgEntries = Object.entries(world.damageByWeapon)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
    const dmgTotal = dmgEntries.reduce((s, [, v]) => s + v, 0) || 1
    const sourceLabels: Record<string, { icon: string; name: string }> = {
      effects: { icon: chromeIconHTML('spark', 22), name: t('recapEffects') },
      gim_aura: { icon: chromeIconHTML('spark', 22), name: t('recapAura') },
      gim_orbit: { icon: chromeIconHTML('spark', 22), name: t('recapOrbit') },
      gim_volley: { icon: chromeIconHTML('spark', 22), name: t('recapVolley') },
      harvest: { icon: chromeIconHTML('spark', 22), name: t('recapHarvest') },
    }
    const dmgLabel = (key: string): { icon: string; name: string } => {
      if (sourceLabels[key]) return sourceLabels[key]
      const d = WEAPONS[key]
      return d ? { icon: iconHTML(weaponIconSprite(d.id), { size: 24, fallback: d.icon }), name: tName('weapon', d.id, d.name) } : { icon: '•', name: key }
    }
    const dmgRows = dmgEntries
      .slice(0, 6)
      .map(([key, v], i) => {
        const { icon, name } = dmgLabel(key)
        const pct = (v / dmgTotal) * 100
        const mvp = i === 0 ? `<span class="recap-mvp">${t('recapTopWeapon')}</span>` : ''
        return `
          <div class="recap-dmg-row${i === 0 ? ' top' : ''}">
            <span class="recap-dmg-ico">${icon}</span>
            <span class="recap-dmg-name">${name}${mvp}</span>
            <span class="recap-dmg-bar"><span style="width:${pct.toFixed(1)}%"></span></span>
            <span class="recap-dmg-pct">${Math.round(pct)}%</span>
          </div>`
      })
      .join('')
    const dmgHtml = dmgRows ? `<div class="recap-h">${t('recapDamage')}</div><div class="recap-dmg">${dmgRows}</div>` : ''

    // --- cause of death ---
    let deathLine = victory ? t('victorySub') : t('deathSub')
    if (!victory && world.killedBy) {
      const cat = world.killedBy.startsWith('boss_') ? 'boss' : 'enemy'
      const def = ENEMIES[world.killedBy]
      const name = def ? tName(cat, world.killedBy, def.name) : t('recapTheSwarm')
      deathLine = `${chromeIconHTML('kills')} ${t('recapKilledBy')} ${name}`
    }

    this.show(`
      <div class="panel" style="text-align:center;max-width:600px">
        <div class="title" style="font-size:48px">${victory ? t('victory') : t('youDied')}</div>
        <div class="subtitle">${deathLine}</div>
        <div class="summary-stats">
          <div class="k">${t('survived')}</div><div class="v">${fmtTime(world.time)}</div>
          <div class="k">${t('levelReached')}</div><div class="v">${world.player.level}</div>
          <div class="k">${t('kills')}</div><div class="v">${world.kills}</div>
          <div class="k">${t('bossesSlain')}</div><div class="v">${world.bossesKilled}</div>
          <div class="k">${t('biggestHit')}</div><div class="v">${Math.round(world.highestHit)}</div>
          <div class="k">${t('goldBanked')}</div><div class="v" style="color:var(--accent)">${chromeIconHTML('gold')} ${goldEarned}</div>
        </div>
        ${buildHtml}
        ${dmgHtml}
        ${unlockHtml}
        <div class="btn-row">
          <button class="btn primary" data-act="again">${t('playAgain')}</button>
          <button class="btn" data-act="menu">${t('mainMenu')}</button>
        </div>
      </div>
    `)
    this.overlay.querySelector('[data-act=again]')!.addEventListener('click', h.again)
    this.overlay.querySelector('[data-act=menu]')!.addEventListener('click', h.menu)
  }
}

export { fmtTime, xpForLevel, CHARACTERS }
